import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { GeneratorService, isDefinedNotNull, isUndefinedOrNull } from '@psnb/services';
import { ActivatedRoute } from '@angular/router';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FuelTankEntity, GeneratorSearchDTO, ModelTypeDTO, TrailerEntity } from '@psnb/psnb-api';
import { PowerSupplyAssetType } from './power-supply-asset-type.enum';
import { PowerSupplyGeneratorAssetDetailsViewModel } from './power-supply-generator-asset-details.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'nwt-power-supply-generator-asset-details',
  styleUrls: ['./power-supply-generator-asset-details.component.scss'],
  templateUrl: './power-supply-generator-asset-details.component.html'
})
export class PowerSupplyGeneratorAssetDetailsComponent implements OnInit, OnDestroy {
  public vm: PowerSupplyGeneratorAssetDetailsViewModel;

  // Private variables
  private destroyed$: Subject<void>;
  private generatorModelId: number | undefined;

  public constructor(
    private cdr: ChangeDetectorRef,
    private generatorService: GeneratorService,
    private route: ActivatedRoute
  ) {
    this.destroyed$ = new Subject<void>();
  }

  public ngOnInit(): void {
    this.vm = {
      fuelTankData: undefined,
      generatorData: undefined,
      generatorMinutesRuntime: undefined,
      params: {
        assetId: undefined,
        assetType: undefined,
        dbId: undefined,
        fuelTankId: undefined,
        generatorId: undefined,
        trailerId: undefined
      },
      PowerSupplyAssetType,
      progressAfterLoad: false,
      showProgressBar: false,
      trailerData: undefined,
      trailerName: undefined
    };

    this.route.queryParams
      .pipe(
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe((params: { [key: string]: string } & { assetType?: PowerSupplyAssetType }): void => {
        this.vm.params.assetId = params.assetId;
        this.vm.params.assetType = params.assetType;
        this.vm.params.fuelTankId = params.fuelTankId;
        this.vm.params.trailerId = params.trailerId;
        this.vm.params.generatorId = params.generatorId;
        this.vm.params.dbId = params.dbId;

        // Load asset data based on query parameters
        this.loadAssetData();
        this.cdr.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  private loadAssetData(): void {
    switch (this.vm.params.assetType) {
      case PowerSupplyAssetType.Generator:
        this.loadGeneratorData();
        break;
      case PowerSupplyAssetType.Trailer:
        this.loadTrailerData();
        break;
      case PowerSupplyAssetType.FuelTank:
        this.loadFuelTankData();
        break;
      default:
        break;
    }
  }

  private loadGeneratorData(): void {
    const generatorId: string = this.vm.params.generatorId ?? this.vm.params.dbId;
    if (isUndefinedOrNull(generatorId)) {
      this.vm.showProgressBar = false;
      this.vm.progressAfterLoad = true;
      this.cdr.detectChanges();
      return;
    }

    this.vm.showProgressBar = true;
    this.cdr.detectChanges();

    this.generatorService.generatorSearchByAssetId(generatorId)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showProgressBar = false;
          this.vm.progressAfterLoad = true;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: GeneratorSearchDTO[]): void => {
          this.vm.generatorData = response[0];
          this.generatorModelId = this.vm.generatorData?.generatorModelId;

          // If we have a model id, fetch models and find the matching runtime
          if (isDefinedNotNull(this.generatorModelId)) {
            this.getGeneratorModelInformation(this.generatorModelId);
          }

          this.loadTrailerName();
        }
      );
  }

  private loadTrailerData(): void {
    const trailerId: string = this.vm.params.trailerId ?? this.vm.params.dbId;
    if (isUndefinedOrNull(trailerId)) {
      this.vm.showProgressBar = false;
      this.vm.progressAfterLoad = true;
      this.cdr.detectChanges();
      return;
    }

    this.vm.showProgressBar = true;
    this.cdr.detectChanges();

    this.generatorService.trailerSearchById(trailerId)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showProgressBar = false;
          this.vm.progressAfterLoad = true;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: TrailerEntity[]): void => {
          this.vm.trailerData = response[0];
          this.cdr.detectChanges();
        }
      );
  }

  private loadFuelTankData(): void {
    const fuelTankId: string = this.vm.params.fuelTankId ?? this.vm.params.dbId;
    if (isUndefinedOrNull(fuelTankId)) {
      this.vm.showProgressBar = false;
      this.vm.progressAfterLoad = true;
      this.cdr.detectChanges();
      return;
    }

    this.vm.showProgressBar = true;
    this.cdr.detectChanges();

    this.generatorService.fuelTankSearchById(fuelTankId)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.showProgressBar = false;
          this.vm.progressAfterLoad = true;
          this.cdr.detectChanges();
        })
      )
      .subscribe(
        (response: FuelTankEntity[]): void => {
          this.vm.fuelTankData = response[0];
          this.cdr.detectChanges();
        }
      );
  }

  private loadTrailerName(): void {
    const trailerId: number = this.vm.generatorData?.trailerId;
    if (isUndefinedOrNull(trailerId)) {
      return;
    }

    this.generatorService.trailerSearchById(trailerId.toString())
      .pipe(
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe((trailerResponse: TrailerEntity[]): void => {
        this.vm.trailerName = trailerResponse[0]?.trailerName;
        this.cdr.detectChanges();
      });
  }

  private getGeneratorModelInformation(modelId: number): void {
    this.generatorService.getModelTypesService()
      .pipe(
        take(1),
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response: ModelTypeDTO[]): void => {
          // response is expected to be an array of ModelType / ModelTypeDTO
          const models: ModelTypeDTO[] = Array.isArray(response) ? response : (response ? [response] : []);
          const matched: ModelTypeDTO = models.find((model: ModelTypeDTO): boolean =>
            // Some model lists use 'id' or 'modelId' or similar; check common keys
            isDefinedNotNull(model) && model.id === modelId);

          if (isDefinedNotNull(matched)) {
            // minutesRuntime is the field used elsewhere in the app
            this.vm.generatorMinutesRuntime = isUndefinedOrNull(matched.minutesRuntime)
              ? undefined
              : matched.minutesRuntime;
          } else {
            this.vm.generatorMinutesRuntime = undefined;
          }

          this.cdr.detectChanges();
        }
      );
  }
}
